import { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { useErrorHandler } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../../context/Auth';

import NFTCollectionCard from './NFTCollectionCard';
import NFTCollectionDetails from './NFTCollectionDetails';
import { Loading } from '../../UI';

import {
  ListStrategyNftCollectionsResponse,
  StrategyNFTCollection,
} from './types';
import { LIST_STRATEGY_NFT_COLLECTIONS } from '../../../http/apollo-queries';

import { GridWidget } from '../types';
import { useParams } from 'react-router-dom';
import { useUserStrategies } from '../../../context/UserStrategies';
import PlaceholderWidget from '../Placeholder/Placeholder';

export const NFTWidget = ({ width, height }: GridWidget) => {
  // Hooks
  const { t } = useTranslation();
  const { strategies } = useUserStrategies();

  const { id: strategyId } = useParams();

  const strategy = strategies.find((s) => s.id === strategyId)!;

  const { user } = useAuth();
  const onError = useErrorHandler();

  // Queries
  const {
    data: {
      listStrategyNftCollections: { items: strategyNftCollections = [] } = {},
    } = {},
    loading,
    refetch: refetchNftColections,
  } = useQuery<ListStrategyNftCollectionsResponse>(
    LIST_STRATEGY_NFT_COLLECTIONS,
    {
      variables: { profileId: user?.profile?.id, strategyId: strategy?.id },
      onError,
    }
  );

  // State
  const [selectedStrategyNftCollection, setSelectedStrategyNftCollection] =
    useState<StrategyNFTCollection | null>(null);

  useEffect(() => {
    refetchNftColections().then(() => setSelectedStrategyNftCollection(null));
  }, [strategy.strategyAssets.items.length]);

  return selectedStrategyNftCollection ? ( // User has clicked specific collection
    <NFTCollectionDetails
      strategyNftCollection={selectedStrategyNftCollection}
      goBack={() => setSelectedStrategyNftCollection(null)}
      width={width}
    />
  ) : loading ? (
    <div className={'w-full flex justify-center mt-2'}>
      <Loading status={true} size={'large'} />
    </div>
  ) : (
    <div className="h-full overflow-y-auto pb-4">
      {strategyNftCollections?.length ? (
        <>
          <h2 className="text-md font-semibold py-3 px-4">
            {t(`widget.nft.yourCollections`)}
          </h2>
          <div className={`grid grid-cols-${width} px-4 gap-4`}>
            {strategyNftCollections?.map((strategyNftCollection) => (
              <NFTCollectionCard
                strategyNftCollection={strategyNftCollection}
                width={width}
                key={strategyNftCollection.id}
                onClick={() =>
                  setSelectedStrategyNftCollection(strategyNftCollection)
                }
              />
            ))}
          </div>
        </>
      ) : (
        <PlaceholderWidget
          title={t(`widget.nft.noCollections`)}
          width={width}
          height={height}
        />
      )}
    </div>
  );
};
